import type { ReactNode } from "react";
import { EmptyState } from "./EmptyState";
import {
  ERROR_STATE_DEFAULT_DESCRIPTION,
  ERROR_STATE_DEFAULT_RETRY,
  ERROR_STATE_DEFAULT_TITLE,
} from "@/lib/i18n/errors";

export interface ErrorStateProps {
  title?: string;
  description?: string;
  /** Retry CTA — typically re-runs the failed query. */
  onRetry?: () => void;
  /** Custom retry button label. */
  retryLabel?: string;
  className?: string;
  icon?: ReactNode;
}

/**
 * P5 — ErrorState.
 *
 * Per `component-api.md §2 P5`:
 *   - EmptyState shell with error variant
 *   - icon uses `!`, halo uses red-toned background
 *   - Provides retry CTA
 *
 * Per W2.3 outcome #4 DoD: defaults come from `lib/i18n/errors.ts` so
 * cross-cutting error strings live in one place.
 */
export function ErrorState({
  title = ERROR_STATE_DEFAULT_TITLE,
  description = ERROR_STATE_DEFAULT_DESCRIPTION,
  onRetry,
  retryLabel = ERROR_STATE_DEFAULT_RETRY,
  className,
  icon,
}: ErrorStateProps) {
  const errorIcon = icon ?? <span aria-hidden>!</span>;
  return (
    <EmptyState
      halo="partner"
      icon={errorIcon}
      title={title}
      description={description}
      cta={
        onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="tap-target px-4 h-11 rounded-md bg-partner-strong text-paper hover:bg-partner transition-colors duration-quick ease-paper"
          >
            {retryLabel}
          </button>
        ) : null
      }
      className={className}
    />
  );
}
